import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, NgZone, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../../environments/environment';
import { MdbModalService } from 'mdb-angular-ui-kit/modal';
import {
  AdminMailAccountsService,
} from '../../../../services/admin/admin-mail-accounts.service';
import { MailAccountModel } from '../../../../models/admin/admin-mail-accounts.model';
import { StatsService } from '../../../../services/mailarchiv/stats.service';
import { StatsDisplay } from '../../../../models/mailarchiv/stats.model';
import { UserModel } from '../../../../models/resources/user.model';
import {
  applyAdminPageMeta,
  buildAdminListQuery,
  moveToNextAdminPage,
  moveToPreviousAdminPage,
  moveToPreviousPageAfterDelete,
  resetAdminPagination,
} from '../admin-pagination-state';
import { CreateMailAccountModalComponent } from '../create-mail-account-modal/create-mail-account-modal.component';
import { ConfirmDeleteMailAccountModalComponent } from '../confirm-delete-mail-account-modal/confirm-delete-mail-account-modal.component';
import { FormSelectComponent } from '../../../utils/form-select/form-select.component';
import { ListPaginationBarComponent } from '../../../utils/list-pagination-bar/list-pagination-bar.component';
import { TextSearchComponent } from '../../../utils/text-search/text-search.component';
import { PaginationMeta } from '../../../../models/pagination-meta.model';
import { MdbButtonComponent } from '../../../utils/mdb-button/mdb-button.component';
import { SpinnerComponent } from '../../../utils/spinner/spinner.component';

@Component({
  selector: 'app-admin-mail-accounts',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    FormSelectComponent,
    ListPaginationBarComponent,
    TextSearchComponent,
    MdbButtonComponent,
    SpinnerComponent,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './admin-mail-accounts.component.html',
  styleUrl: './admin-mail-accounts.component.scss',
})
export class AdminMailAccountsComponent implements OnInit {
  users: UserModel[] = [];
  mailAccounts: MailAccountModel[] = [];
  searchTerm = '';
  /** Selected owner user IDs for filter (synced from filterForm on apply). */
  ownerIds: number[] = [];
  currentPage = 1;
  lastPage = 1;
  perPage = 25;
  readonly perPageOptions = [10, 25, 50, 100];
  total = 0;
  loading = false;
  /** True while the mail accounts list is being fetched (initial load, filter, pagination). */
  listLoading = false;
  errorMessage: string | null = null;
  /** ID of mail account for which import was just triggered (used to disable button while request is in flight). */
  importingAccountId: number | null = null;
  /** ID of mail account currently being deleted (used to show loading on the matching delete button). */
  deletingAccountId: number | null = null;
  /** Cache of stats per mail account id (for tooltip on hover). */
  statsByAccountId: Record<number, StatsDisplay | 'loading' | 'error' | undefined> = {};
  /** Account id for which the stats popover is currently visible (hover state). */
  hoveredStatsAccountId: number | null = null;

  readonly displayDateTimeFormat = environment.displayDateTimeFormat;
  readonly filterForm: FormGroup;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);

  constructor(
    private adminMailAccountsService: AdminMailAccountsService,
    private statsService: StatsService,
    private modalService: MdbModalService,
    private datePipe: DatePipe,
    private translate: TranslateService,
    fb: FormBuilder,
  ) {
    this.filterForm = fb.group({
      ownerIds: [[] as number[]],
    });
  }

  ngOnInit(): void {
    this.adminMailAccountsService.getUsers()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        this.users = response.data;
        this.cdr.markForCheck();
      });

    this.loadMailAccounts();
  }

  openCreateModal(): void {
    this.openMailAccountModal(undefined);
  }

  openEditModal(account: MailAccountModel): void {
    this.openMailAccountModal(account);
  }

  private openMailAccountModal(account: MailAccountModel | undefined): void {
    const modalRef = this.modalService.open(CreateMailAccountModalComponent, {
      modalClass: 'modal-lg',
      data: { users: this.users, account: account ?? null },
      ignoreBackdropClick: true,
    });

    modalRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((updated: MailAccountModel | undefined) => {
        if (updated) {
          const index = this.mailAccounts.findIndex((m) => m.id === updated.id);
          if (index >= 0) {
            this.mailAccounts = this.mailAccounts.map((m) => (m.id === updated.id ? updated : m));
          } else {
            this.mailAccounts = [...this.mailAccounts, updated];
          }
          this.cdr.markForCheck();
        }
      });
  }

  loadMailAccounts(resetPage = false): void {
    if (resetPage) {
      resetAdminPagination(this);
    }

    this.listLoading = true;
    this.adminMailAccountsService.getMailAccounts(buildAdminListQuery(this))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.mailAccounts = response.data;
          applyAdminPageMeta(this, response.meta, response.data.length);
          this.listLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.listLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  applyFilters(): void {
    this.ownerIds = (this.filterForm.get('ownerIds')?.value ?? []) as number[];
    this.loadMailAccounts(true);
  }

  nextPage(): void {
    if (!moveToNextAdminPage(this)) {
      return;
    }
    this.loadMailAccounts();
  }

  previousPage(): void {
    if (!moveToPreviousAdminPage(this)) {
      return;
    }
    this.loadMailAccounts();
  }

  onPerPageChange(value: number): void {
    this.perPage = value;
    this.loadMailAccounts(true);
  }

  ownerName(account: MailAccountModel): string {
    if (account.ownerUserId == null) {
      return '-';
    }
    const user = this.users.find((u) => u.id === account.ownerUserId);
    return user?.username ?? String(account.ownerUserId);
  }

  lastImportDisplay(account: MailAccountModel): string {
    const run = account.lastImportRun;
    if (!run?.startedAt) {
      return '—';
    }
    const dateStr = this.datePipe.transform(run.startedAt, this.displayDateTimeFormat) ?? run.startedAt;
    const statusKey = run.status ? `ADMIN.MAIL_ACCOUNTS.IMPORT_STATUS_${(run.status as string).toUpperCase()}` : '';
    const status = statusKey ? (this.translate.instant(statusKey) !== statusKey ? this.translate.instant(statusKey) : run.status) : run.status ?? '';
    return status ? `${dateStr} — ${status}` : dateStr;
  }

  onStatsCellMouseEnter(accountId: number): void {
    this.ngZone.run(() => {
      this.hoveredStatsAccountId = accountId;
      this.loadStatsForAccount(accountId);
      this.cdr.markForCheck();
    });
    setTimeout(() => this.cdr.markForCheck(), 0);
  }

  onStatsCellMouseLeave(): void {
    this.hoveredStatsAccountId = null;
    this.cdr.markForCheck();
  }

  /** Returns StatsDisplay for the account when loaded; null when loading, error, or not loaded. */
  getAccountStatsDisplay(accountId: number): StatsDisplay | null {
    const v = this.statsByAccountId[accountId];
    return typeof v === 'object' && v !== null && 'numberOfDocuments' in v ? v : null;
  }

  loadStatsForAccount(accountId: number): void {
    if (this.statsByAccountId[accountId] !== undefined) {
      return;
    }
    this.statsByAccountId = { ...this.statsByAccountId, [accountId]: 'loading' };
    this.cdr.markForCheck();
    this.statsService.getStatsForAccount(accountId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (stats) => {
          this.statsByAccountId = { ...this.statsByAccountId, [accountId]: stats };
          this.cdr.markForCheck();
        },
        error: () => {
          this.statsByAccountId = { ...this.statsByAccountId, [accountId]: 'error' };
          this.cdr.markForCheck();
        },
      });
  }

  triggerImport(account: MailAccountModel): void {
    if (this.loading || this.importingAccountId != null) {
      return;
    }
    this.importingAccountId = account.id;
    this.errorMessage = null;
    this.adminMailAccountsService.triggerImport(account.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: () => {
        this.importingAccountId = null;
        this.errorMessage = null;
        this.cdr.markForCheck();
      },
      error: (err: { status?: number; error?: { message?: string } }) => {
        this.importingAccountId = null;
        if (err?.status === 409) {
          this.errorMessage = 'ADMIN.MAIL_ACCOUNTS.IMPORT_ALREADY_RUNNING';
        } else {
          this.errorMessage = 'ADMIN.MAIL_ACCOUNTS.IMPORT_FAILED';
        }
        this.cdr.markForCheck();
      },
    });
  }

  openDeleteConfirmModal(account: MailAccountModel): void {
    if (this.loading) {
      return;
    }
    const modalRef = this.modalService.open(ConfirmDeleteMailAccountModalComponent, {
      data: { accountName: account.name ?? '-' },
      ignoreBackdropClick: true,
    });
    modalRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.deleteMailAccount(account);
        }
      });
  }

  deleteMailAccount(account: MailAccountModel): void {
    if (this.loading) {
      return;
    }

    this.errorMessage = null;
    this.loading = true;
    this.deletingAccountId = account.id;
    this.cdr.markForCheck();
    this.adminMailAccountsService.deleteMailAccount(account.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: () => {
        this.mailAccounts = this.mailAccounts.filter((m) => m.id !== account.id);
        if (moveToPreviousPageAfterDelete(this, this.mailAccounts.length)) {
          this.loading = false;
          this.deletingAccountId = null;
          this.loadMailAccounts();
          return;
        }
        this.loading = false;
        this.deletingAccountId = null;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.deletingAccountId = null;
        this.errorMessage = 'ADMIN.MAIL_ACCOUNTS.SAVE_FAILED';
        this.cdr.markForCheck();
      },
    });
  }

  /** Build PaginationMeta for list-pagination-bar from current state. */
  get paginationMeta(): PaginationMeta {
    return {
      current_page: this.currentPage,
      last_page: this.lastPage,
      per_page: this.perPage,
      total: this.total,
    };
  }
}
